import { sameScope, completeScope } from './deviceScope';

/**
 * Durable bounded photo queue (A3).
 *
 * Follows the shape of frontend/src/offline/outbox.js (which is not modified):
 * IndexedDB, one store, an explicit status per row. Differences are deliberate:
 *
 *  - the id is a uuid and doubles as the Idempotency-Key sent to the server;
 *  - the JPEG is stored as a base64 string, which structured-clones everywhere
 *    (Blob and ArrayBuffer support in IndexedDB varies) and is exactly what the
 *    API transmits, so no re-encoding happens at send time;
 *  - storage is bounded by MAX_QUEUE_BYTES with a visible quota failure;
 *  - bytes are deleted only after the server confirms the capture.
 *
 * The row is created from ALREADY SANITIZED bytes: stripGps runs first, the hash
 * is taken over exactly what will be transmitted, and the unsanitized original is
 * never retained. See sanitize.js.
 */

export const DB_NAME = 'foodops-photo-queue';
export const STORE = 'photos';
export const DB_VERSION = 1;

/** Bounded storage. A phone with a full disk must not lose the queue silently. */
export const MAX_QUEUE_BYTES = 60 * 1024 * 1024; // 60 MB
export const MAX_QUEUE_ITEMS = 200;
export const MAX_ATTEMPTS = 5;

export const STATUS = Object.freeze({
  QUEUED: 'queued',
  SENDING: 'sending',
  SENT: 'sent',
  FAILED: 'failed',
});

/** HTTP codes that will never succeed on a retry. */
export const PERMANENT_HTTP = new Set([400, 403, 404, 409, 410, 413, 415, 422]);

export function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('organization_id', 'organization_id', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode) {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function reqToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Uint8Array → base64, chunked so a large photo cannot blow the call stack. */
export function bytesToBase64(bytes) {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** base64 → Uint8Array. */
export function base64ToBytes(b64) {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

/**
 * Blob → ArrayBuffer. Blob.arrayBuffer() is absent in older jsdom (16.x), so
 * FileReader is the fallback; both produce the same bytes.
 */
export async function blobToArrayBuffer(blob) {
  if (blob && typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('could not read the blob'));
    reader.readAsArrayBuffer(blob);
  });
}

export async function sha256Hex(buffer) {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  throw new Error('crypto.subtle is unavailable; cannot hash the queued bytes');
}

export function newId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `photo-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Total bytes currently held, so the caller can show usage and refuse an add. */
export async function queueUsage() {
  const rows = await listAll();
  return {
    bytes: rows.reduce((sum, r) => sum + (r.size_bytes || 0), 0),
    items: rows.length,
  };
}

export class QueueQuotaError extends Error {
  constructor(usage) {
    super('The photo queue is full. Upload or discard photos before adding more.');
    this.name = 'QueueQuotaError';
    this.usage = usage;
  }
}

/**
 * Add a sanitized photo. Caller passes bytes already stripped of GPS metadata.
 * Returns the stored row (including the sha256 that will be sent as original_sha256).
 */
export async function addPhoto({
  blob,
  bytes = null,
  organization_id,
  location_id,
  user_id,
  capture_type = 'delivery_note',
  receipt_id = null,
  device_time = null,
  id = null,
}) {
  if (!blob && !bytes) throw new Error('a blob is required');
  if (!completeScope({ orgId: organization_id, userId: user_id, locationId: location_id })) {
    throw new Error('Authenticated user, organization and site are required');
  }

  const buffer = bytes || (await blobToArrayBuffer(blob));
  const byteView = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const usage = await queueUsage();
  const size = byteView.byteLength;
  if (usage.bytes + size > MAX_QUEUE_BYTES || usage.items + 1 > MAX_QUEUE_ITEMS) {
    throw new QueueQuotaError(usage);
  }

  const row = {
    id: id || newId(),
    content_base64: bytesToBase64(byteView),
    content_type: (blob && blob.type) || 'image/jpeg',
    sha256: await sha256Hex(byteView),
    size_bytes: size,
    device_time: device_time || new Date().toISOString(),
    organization_id,
    location_id: location_id || null,
    user_id: user_id || null,
    capture_type,
    receipt_id,
    status: STATUS.QUEUED,
    attempts: 0,
    last_error: null,
    enqueued_at: new Date().toISOString(),
  };
  const db = await openDb();
  await reqToPromise(tx(db, 'readwrite').put(row));
  return row;
}

export async function listAll() {
  const db = await openDb();
  const rows = await reqToPromise(tx(db, 'readonly').getAll());
  return (rows || []).sort((a, b) => String(a.enqueued_at).localeCompare(String(b.enqueued_at)));
}

/** The queued bytes as a Uint8Array, for hashing or display. */
export function rowBytes(row) {
  return base64ToBytes(row.content_base64 || '');
}

export async function listQueued() {
  return (await listAll()).filter((r) => r.status === STATUS.QUEUED || r.status === STATUS.FAILED);
}

export async function getPhoto(id) {
  const db = await openDb();
  return reqToPromise(tx(db, 'readonly').get(id));
}

export async function updatePhoto(id, patch) {
  const db = await openDb();
  const existing = await reqToPromise(tx(db, 'readonly').get(id));
  if (!existing) return null;
  const next = { ...existing, ...patch };
  await reqToPromise(tx(db, 'readwrite').put(next));
  return next;
}

/** Remove the bytes. Only called after the server confirmed the capture. */
export async function removePhoto(id) {
  const db = await openDb();
  return reqToPromise(tx(db, 'readwrite').delete(id));
}

/**
 * Manual retry for a row the operator chose to re-send after a permanent failure.
 * Clears the exhausted attempt count so the next drain picks it up.
 */
export async function retryFailed(id) {
  const row = await getPhoto(id);
  if (!row) return null;
  return updatePhoto(id, { status: STATUS.QUEUED, attempts: 0, permanent: false, last_error: null });
}

/** Atomically claim/discard a row; resolve only after the durable transaction commits.
 * A sending row cannot be discarded: its server outcome is not yet known.
 */
async function changeQueuedPhoto(id, scope, discard = false) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite');
    const store = transaction.objectStore(STORE);
    let result = null;
    let failure = null;
    const request = store.get(id);
    request.onsuccess = () => {
      const row = request.result;
      if (!row) failure = new Error('Photo is no longer queued; it may already have uploaded. No server record was deleted.');
      else if (!sameScope(row, scope)) failure = new Error('Photo belongs to another or unknown scope.');
      else if (![STATUS.QUEUED, STATUS.FAILED].includes(row.status)) failure = new Error('Photo is uploading; wait for its result before discarding.');
      else {
        result = row;
        if (discard) store.delete(id);
        else store.put({ ...row, status: STATUS.SENDING, attempts: row.attempts + 1 });
      }
    };
    transaction.oncomplete = () => { db.close(); if (failure) reject(failure); else resolve(result); };
    transaction.onerror = transaction.onabort = () => { db.close(); reject(transaction.error || new Error('Photo storage transaction failed')); };
  });
}

export async function discardPhoto(id, scope) {
  return changeQueuedPhoto(id, scope, true);
}

/**
 * Upload one row. The Idempotency-Key is the row id, so a retry after a lost
 * response returns the first capture instead of creating a second.
 */
export async function sendPhoto(row, { apiUrl, token, orgId, locationId }) {
  const content_base64 = row.content_base64;
  const res = await fetch(`${apiUrl || ''}/api/receiving/goods-in/captures`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'Idempotency-Key': row.id,
      ...(orgId ? { 'X-Organization-Id': orgId } : {}),
      ...(locationId ? { 'X-Location-Id': locationId } : {}),
    },
    body: JSON.stringify({
      content_base64,
      content_type: row.content_type || 'image/jpeg',
      capture_type: row.capture_type,
      receipt_id: row.receipt_id,
      original_sha256: row.sha256,
      strip_gps: true,
    }),
  });

  if (res.ok) return { ok: true, data: await res.json() };
  const detail = await res.text().catch(() => '');
  return { ok: false, status: res.status, detail, permanent: PERMANENT_HTTP.has(res.status) };
}

/**
 * Drain the queue in order. Transient failures back off and stay queued; a
 * permanent failure stops retrying that row and is surfaced for manual retry.
 */
export async function flushQueue({
  apiUrl,
  token,
  orgId,
  userId,
  locationId,
  maxAttempts = MAX_ATTEMPTS,
  send = sendPhoto,
  isCurrent = () => true,
} = {}) {
  const rows = await listAll();
  const results = [];
  for (const row of rows) {
    if (row.status === STATUS.SENT) continue;
    if (!token || !isCurrent()) break;
    if (!sameScope(row, { userId, orgId, locationId })) {
      results.push({ id: row.id, status: row.status, skipped: 'scope_mismatch_or_legacy' });
      continue;
    }
    if (row.attempts >= maxAttempts) {
      results.push({ id: row.id, status: STATUS.FAILED, reason: 'max_attempts' });
      continue;
    }

    try { await changeQueuedPhoto(row.id, { userId, orgId, locationId }); }
    catch { continue; } // Discard or another drain won the transaction.
    if (!isCurrent()) {
      await updatePhoto(row.id, { status: STATUS.QUEUED });
      break;
    }
    let outcome;
    try {
      outcome = await send(row, { apiUrl, token, orgId, locationId });
    } catch (err) {
      outcome = { ok: false, transient: true, detail: String(err) };
    }

    if (outcome.ok) {
      // Bytes go only after the server confirmed.
      await removePhoto(row.id);
      results.push({ id: row.id, status: STATUS.SENT, capture: outcome.data });
    } else if (outcome.permanent) {
      // Exhaust the attempts so an automatic drain leaves it alone; a manual
      // retry (retryFailed) is what clears this.
      await updatePhoto(row.id, {
        status: STATUS.FAILED,
        permanent: true,
        attempts: maxAttempts,
        last_error: outcome.detail || `HTTP ${outcome.status}`,
      });
      results.push({ id: row.id, status: STATUS.FAILED, detail: outcome.detail, http: outcome.status });
    } else {
      // Transient: stay queued, keep the same key, try again later.
      await updatePhoto(row.id, {
        status: STATUS.QUEUED,
        last_error: outcome.detail || 'network',
        next_attempt_at: new Date(Date.now() + backoffMs(row.attempts + 1)).toISOString(),
      });
      results.push({ id: row.id, status: STATUS.QUEUED, retry_in_ms: backoffMs(row.attempts + 1) });
    }
  }
  return results;
}

/** Exponential backoff with a ceiling, in milliseconds. */
export function backoffMs(attempt) {
  return Math.min(60_000, 1_000 * 2 ** Math.max(0, attempt - 1));
}

/**
 * Suspend everything for a tenant/site that is no longer the active one. Called
 * on logout and on an account or site switch. No bearer token is ever stored.
 */
export async function suspendForScope({ userId, orgId, locationId }) {
  const rows = await listAll();
  let suspended = 0;
  for (const row of rows) {
    const sameScope =
      (!row.user_id || row.user_id === userId)
      && (!row.organization_id || row.organization_id === orgId)
      && (!row.location_id || row.location_id === locationId);
    if (!sameScope && row.status !== STATUS.SENT) {
      await updatePhoto(row.id, { status: STATUS.QUEUED, suspended: true });
      suspended += 1;
    }
  }
  return suspended;
}

/** Rows awaiting upload, for a badge. */
export async function pendingCount() {
  return (await listQueued()).length;
}
